import _ from 'lodash';
import { useEffect, useReducer } from 'react';
import {
  Button,
  Checkbox,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Switch,
  Text,
  Textarea,
} from '@optiaxiom/react';
import { IconXmark } from '@optimizely/axiom-icons';

import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { UnifiedAgent } from '@frontend/types/agent';

import { useAgentDirectoryDetails } from '../hooks/useAgentDirectory';
import { AgentDirectoryDetails } from '../services/agent-directory';

import { styles } from './PublishAgentSidebar.css';

const SPECIALIZED_AGENT_LOCKED_FIELD_OPTIONS = [
  { value: 'agent_name', label: 'Agent Name' },
  { value: 'agent_description', label: 'Agent Description' },
  { value: 'prompt_template', label: 'Prompt Template' },
  { value: 'variables', label: 'Variables' },
  { value: 'tools', label: 'Tools' },
  { value: 'inference', label: 'Inference' },
  { value: 'creativity', label: 'Creativity' },
  { value: 'files', label: 'Files' },
  { value: 'output_data_type', label: 'Output Data Type' },
  { value: 'output_description', label: 'Output Description' },
] as const;

const WORKFLOW_AGENT_LOCKED_FIELD_OPTIONS = [
  { value: 'agent_name', label: 'Agent Name' },
  { value: 'agent_description', label: 'Agent Description' },
  // there can be more fields in future for workflow agents
] as const;

// Form state type based on editable fields from AgentDirectoryDetails
export type PublishFormState = {
  is_featured: boolean;
  is_hidden: boolean;
  details_url: string;
  release_notes: string;
  etag: string;
  tagsInput: string; // Keep as string for UI convenience
  // Opti managed agent fields
  is_system_agent: boolean;
  locked_fields: string[];
};

// Reducer action types
type FormAction =
  | { type: 'HYDRATE_FORM'; payload: AgentDirectoryDetails }
  | { type: 'UPDATE_FIELD'; field: keyof PublishFormState; value: string | boolean | string[] }
  | { type: 'TOGGLE_LOCKED_FIELD'; field: string }
  | { type: 'RESET_FORM' };

// Initial form state
const initialFormState: PublishFormState = {
  is_featured: false,
  is_hidden: false,
  details_url: '',
  release_notes: '',
  etag: '',
  tagsInput: '',
  // Opti managed agent fields
  is_system_agent: false,
  locked_fields: [],
};

// Form reducer function
function formReducer(state: PublishFormState, action: FormAction): PublishFormState {
  switch (action.type) {
    case 'HYDRATE_FORM':
      return {
        is_featured: action.payload.is_featured,
        is_hidden: action.payload.is_hidden,
        details_url: action.payload.details_url || '',
        release_notes: action.payload.release_notes || '',
        etag: action.payload.etag || '',
        tagsInput: action.payload.tags?.map(tag => tag.name).join(', ') || '',
        // Opti managed agent fields
        is_system_agent: action.payload.is_system_agent ?? false,
        locked_fields: action.payload.locked_fields || [],
      };
    case 'UPDATE_FIELD':
      return {
        ...state,
        [action.field]: action.value,
      };
    case 'TOGGLE_LOCKED_FIELD': {
      const currentFields = state.locked_fields;
      const fieldValue = action.field;
      const newFields = _.xor(currentFields, [fieldValue]);
      return {
        ...state,
        locked_fields: newFields,
      };
    }
    case 'RESET_FORM':
      return initialFormState;
    default:
      return state;
  }
}

export type PublishFormData = {
  is_featured: boolean;
  is_hidden: boolean;
  release_notes: string;
  tags: string[];
  etag?: string;
  details_url?: string;
  // Opti managed agent fields
  is_system_agent: boolean;
  locked_fields: string[];
};

type PublishAgentSidebarProps = {
  open: boolean;
  agent: UnifiedAgent | null;
  onClose: () => void;
  onPublish: (data: PublishFormData) => void;
  isPublishing: boolean;
};

export function PublishAgentSidebar({
  open,
  agent,
  onClose,
  onPublish,
  isPublishing,
}: PublishAgentSidebarProps) {
  const [formState, dispatch] = useReducer(formReducer, initialFormState);

  // Fetch existing agent details from directory if available
  const { data: agentDetails } = useAgentDirectoryDetails(agent?.agent_id || null);

  // Hydrate form with existing agent directory data
  useEffect(() => {
    if (agentDetails) {
      dispatch({ type: 'HYDRATE_FORM', payload: agentDetails });
    }
  }, [agentDetails]);

  const handleSubmit = () => {
    // Convert comma-separated tags to array
    const tagsArray = formState.tagsInput
      .split(',')
      .map((tag: string) => tag.trim())
      .filter((tag: string) => tag.length > 0);

    onPublish({
      is_featured: formState.is_featured,
      is_hidden: formState.is_hidden,
      release_notes: formState.release_notes,
      tags: tagsArray,
      etag: formState.etag || undefined,
      details_url: formState.details_url || undefined,
      // Opti managed agent fields (only for specialized agents)
      is_system_agent: formState.is_system_agent,
      locked_fields: formState.locked_fields,
    });
  };

  const handleClose = () => {
    dispatch({ type: 'RESET_FORM' });
    onClose();
  };

  const LOCKED_FIELD_OPTIONS =
    agent?.agent_type === 'specialized'
      ? SPECIALIZED_AGENT_LOCKED_FIELD_OPTIONS
      : WORKFLOW_AGENT_LOCKED_FIELD_OPTIONS;

  return (
    <Sidebar open={open} dock="right" initialWidth={400} minWidth={350} maxWidth={600}>
      <Flex flexDirection="column" gap="16" p="24">
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          className={styles.header}
        >
          <Text fontWeight="600" fontSize="lg">
            Publish Agent
          </Text>
          <Button icon={<IconXmark size="16" />} appearance="default" onClick={handleClose} />
        </Flex>

        {agent && (
          <Flex flexDirection="column" gap="16">
            <Field label="Agent">
              <Text fontSize="sm" color="fg.secondary">
                {agent.name}
              </Text>
            </Field>

            <Field label="ETag" info="Version identifier for this published agent">
              <Input
                value={formState.etag}
                readOnly
                placeholder="ETag will appear here for published agents"
              />
            </Field>

            <Field
              label="Details URL"
              info="Optional URL with additional information about this agent"
            >
              <Input
                value={formState.details_url}
                onChange={e =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'details_url', value: e.target.value })
                }
                placeholder="https://example.com/agent-documentation"
              />
            </Field>

            <Field label="Visibility Options">
              <Flex flexDirection="column" gap="16">
                <Flex flexDirection="row" justifyContent="space-between" alignItems="start">
                  <Flex flexDirection="column" gap="2">
                    <Text fontWeight="500" fontSize="md">
                      Featured
                    </Text>
                    <Text fontSize="sm" color="fg.tertiary">
                      Mark this agent as featured in the directory
                    </Text>
                  </Flex>
                  <Switch
                    checked={formState.is_featured}
                    onCheckedChange={value =>
                      dispatch({ type: 'UPDATE_FIELD', field: 'is_featured', value })
                    }
                  />
                </Flex>

                <Flex flexDirection="row" justifyContent="space-between" alignItems="start">
                  <Flex flexDirection="column" gap="2">
                    <Text fontWeight="500" fontSize="md">
                      Hidden
                    </Text>
                    <Text fontSize="sm" color="fg.tertiary">
                      Hide this agent from public browse
                    </Text>
                  </Flex>
                  <Switch
                    checked={formState.is_hidden}
                    onCheckedChange={value =>
                      dispatch({ type: 'UPDATE_FIELD', field: 'is_hidden', value })
                    }
                  />
                </Flex>
              </Flex>
            </Field>

            <Field label="Tags">
              <Input
                value={formState.tagsInput}
                onChange={e =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'tagsInput', value: e.target.value })
                }
                placeholder="marketing, automation, content"
              />
              <Text fontSize="sm" color="fg.tertiary">
                Enter tags separated by commas
              </Text>
            </Field>

            <Field label="Release Notes">
              <Textarea
                value={formState.release_notes}
                onChange={e =>
                  dispatch({ type: 'UPDATE_FIELD', field: 'release_notes', value: e.target.value })
                }
                placeholder="Describe what's new or changed in this version..."
                resize="vertical"
                rows={4}
              />
            </Field>

            {/* Opti Managed Agent Section - Only for specialized agents */}
            <Disclosure>
              <DisclosureTrigger>Opti Managed Agent Settings</DisclosureTrigger>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16" pt="16" pl="24">
                  {/* System Agent */}
                  <Flex flexDirection="row" justifyContent="space-between" alignItems="start">
                    <Flex flexDirection="column" gap="2">
                      <Text fontWeight="500" fontSize="md">
                        System Agent
                      </Text>
                      <Text fontSize="sm" color="fg.tertiary">
                        Mark this as a system-managed agent.
                      </Text>
                    </Flex>
                    <Switch
                      checked={formState.is_system_agent}
                      onCheckedChange={value =>
                        dispatch({ type: 'UPDATE_FIELD', field: 'is_system_agent', value })
                      }
                    />
                  </Flex>

                  {/* Protected Fields */}
                  {formState.is_system_agent && (
                    <Field
                      label="Protected Fields"
                      info="Select fields that users cannot edit when installing this agent"
                    >
                      <Flex flexDirection="column" gap="8">
                        <Checkbox
                          checked={formState.locked_fields.length === LOCKED_FIELD_OPTIONS.length}
                          indeterminate={
                            formState.locked_fields.length > 0 &&
                            formState.locked_fields.length < LOCKED_FIELD_OPTIONS.length
                          }
                          onCheckedChange={checked => {
                            if (checked) {
                              dispatch({
                                type: 'UPDATE_FIELD',
                                field: 'locked_fields',
                                value: LOCKED_FIELD_OPTIONS.map(o => o.value),
                              });
                            } else {
                              dispatch({
                                type: 'UPDATE_FIELD',
                                field: 'locked_fields',
                                value: [],
                              });
                            }
                          }}
                        >
                          All
                        </Checkbox>
                        <Flex flexDirection="column" gap="8" pl="24">
                          {LOCKED_FIELD_OPTIONS.map(option => (
                            <Checkbox
                              key={option.value}
                              checked={formState.locked_fields.includes(option.value)}
                              onCheckedChange={() =>
                                dispatch({ type: 'TOGGLE_LOCKED_FIELD', field: option.value })
                              }
                            >
                              {option.label}
                            </Checkbox>
                          ))}
                        </Flex>
                      </Flex>
                    </Field>
                  )}
                </Flex>
              </DisclosureContent>
            </Disclosure>

            <Button onClick={handleSubmit} disabled={isPublishing} appearance="primary">
              {isPublishing ? 'Publishing...' : 'Publish Agent'}
            </Button>
          </Flex>
        )}
      </Flex>
    </Sidebar>
  );
}
